/**
 * A Config contains and saves configuration information for all aspects of the renderer.
 * Changes to a config can be saved in local storage to preserve user preferences,
 * and configuration settings can be saved and pre-selected based on various aspects of the
 * user's device, such as wether it's a mobile device or not.
 *
 * Configs detect changes via use of the Proxy API to trap set events, which can be slow when
 * used frequently. As such, it's a good idea to only store properties in a config that you
 * anticipate being changed infrequently. Getting the properties frequently should have no penalty.
 */

// Note that this file has a lot more @ts-expect-error comments than usual. This is because we're
// using JS objects in a somewhat unorthodox (but legal) way that TypeScript really doesn't like.
// If I can find a way in the future to be more typesafe I'll take it, but in the meantime this
// is all *Jedi mindtrick handwave* just fine.

const UPDATED_CONFIGS = new Set<Config>();
// How frequently update configs will be cached out to storage.
const CONFIG_UPDATE_INTERVAL = 10 * 1000;

let immutableProperties: WeakMap<object, string[]>;

// Decorator that indicates that the config property will never change after
// initialization. Value must be set in SetDefaults.
export function immutable(target: any, key: string): void {
  if (!immutableProperties) {
    immutableProperties = new WeakMap();
  }
  let immutablePropertyList = immutableProperties.get(target);
  if (!immutablePropertyList) {
    immutablePropertyList = [];
    immutableProperties.set(target.constructor, immutablePropertyList);
  }
  immutablePropertyList.push(key);
}

function isImmutableProperty(target: any, propertyName: string): boolean {
  if (!immutableProperties) { return false; }
  const immutablePropertyList = immutableProperties.get(target);
  if (!immutablePropertyList) { return false; }
  return immutablePropertyList.includes(propertyName);
}

// Proxy handler for config objects to ensure that changing any attribute fires
// the appropriate events and marks the config to be saved
const CONFIG_HANDLER = {
  set(obj: Config, key: string, value: any) {
    if (isImmutableProperty(obj.constructor, key)) {
      throw new Error(`Cannot change @immutable config value ${key} outside of SetDefaults()`);
    }
    // @ts-expect-error
    const changed = obj[key] !== value;
    // @ts-expect-error
    const ret = Reflect.set(...arguments);
    if (changed) {
      obj.fireChangedEvent(key, value);
    }
    return ret;
  },
  get: function (obj: Config, key: string) {
    const result = Reflect.get(obj, key)
    if(typeof result === 'function')
        return result.bind(obj)
    return result
  }
}

const configStringify = (key: string, value: any) => {
  return value instanceof Object && !(value instanceof Array) ?
    Object.keys(value).reduce((out, key) => {
      if (key != 'configRevision') {
        // @ts-expect-error
        out[key] = value[key];
      }
      return out;
    }, {}) : value;
}

function SaveAllConfigs() {
  if (UPDATED_CONFIGS.size) {
    for (const config of UPDATED_CONFIGS) {
      config.saveToStorage();
    }
    UPDATED_CONFIGS.clear();
  }
}

export interface ConfigEventDetail {
  property: string,
  value: any
}

function isMobileBrowser() {
  //@ts-ignore
  if (('userAgentData' in navigator) && ('mobile' in navigator.userAgentData)) {
    //@ts-ignore
    return navigator.userAgentData.mobile;
  }
  // From https://stackoverflow.com/questions/11381673/detecting-a-mobile-browser
  // I think we could do something more targeted here.
  let check = false;
  //@ts-ignore
  (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
  return check;
}

const CONFIG_DEFAULTS: Map<string, any> = new Map();

let updateInterval: number;

export class Config extends EventTarget {
  #loading = false;
  #nextRevision = 1;
  #revision = this.#nextRevision++;
  #configRevisions = new Map<string, number>();

  constructor() {
    super();
  }

  static Create<ConfigType extends Config>(configType: new() => ConfigType, args: any = null): ConfigType {
    const config = new configType();
    config.#loading = true;

    let defaults = CONFIG_DEFAULTS.get(config.configStorageName!);
    if (!defaults) {
      defaults = new configType();
      if (Object.hasOwn(config.constructor, 'SetDefaults')) {
        const deviceDefaults = (config.constructor as any).SetDefaults(isMobileBrowser(), args);
        defaults.#setConfigProperties(deviceDefaults);
      }
      CONFIG_DEFAULTS.set(config.configStorageName!, defaults);
    }

    config.#setConfigProperties(defaults);

    if (config.configStorageName) {
      config.loadFromStorage();
      config.updateFromQueryArgs();
    }
    if (!updateInterval) {
      updateInterval = setInterval(SaveAllConfigs, CONFIG_UPDATE_INTERVAL);
    }
    config.#loading = false;
    return new Proxy(config, CONFIG_HANDLER) as ConfigType;
  }

  get configStorageName(): string | null {
    return this.constructor.name;
  }

  fireChangedEvent(property: string, value: any) {
    if (this.#loading) { return; } // Don't fire a bunch of events while the config is loading.

    this.#updateRevision();

    if (this.configStorageName) {
      UPDATED_CONFIGS.add(this);
    }
    let event = new CustomEvent<ConfigEventDetail>('changed', {
      cancelable: false,
      bubbles: true,
      detail: {
        property,
        value,
      }
    });
    this.dispatchEvent(event);
  }

  #updateRevision() {
    // Get a revision number that is unique to this combination of config values
    const key = JSON.stringify(this, configStringify);
    const cachedRevision = this.#configRevisions.get(key);
    if (cachedRevision) {
      this.#revision = cachedRevision;
    } else {
      this.#revision = this.#nextRevision++;
      this.#configRevisions.set(key, this.#revision);
    }
  }

  get configRevision() { return this.#revision; }

  #getConfigProperties(): any {
    let defaults = CONFIG_DEFAULTS.get(this.configStorageName!);

    // Filter out only properties defined on this config object.
    return Object.keys(defaults).reduce((out, key) => {
      // Don't bother exporting values that match the defaults.
      // @ts-expect-error
      if (!defaults || defaults[key] !== this[key]) {
        // @ts-expect-error
        out[key] = this[key];
      }
      return out;
    }, {});
  }

  #setConfigProperties(obj: any) {
    if (!obj) { return; }
    for (const key of Object.keys(obj)) {
      if (Object.hasOwn(this, key)) {
        // @ts-expect-error
        if (this[key] == key) { continue; }
        // @ts-expect-error
        this[key] = obj[key];
        this.fireChangedEvent(key, obj[key]);
      } else {
        console.warn(`Attempting to set unknown config property: ${key}.`);
      }
    }
  }

  saveToStorage() {
    let storageName = this.configStorageName;
    if (!storageName) { throw new Error('Config does not specify a storage name'); }

    console.info(`Saving updates to Config: ${storageName}, revision ${this.configRevision}`);
    localStorage.setItem(storageName, JSON.stringify(this.#getConfigProperties()));
  }

  loadFromStorage() {
    let storageName = this.configStorageName;
    if (!storageName) { throw new Error('Config does not specify a storage name'); }

    let json = localStorage.getItem(storageName);
    if (!json) { return; }

    let obj: any;
    try {
      obj = JSON.parse(json);
    } catch(err: any) {
      console.warn(`Config loaded from localStorage was not valid json. ${err.message}`);
      return;
    }

    this.#setConfigProperties(obj);
  }

  async loadFromUrl(url: string) {
    const response = await fetch(url);
    let obj: any;
    try {
      obj = await response.json();
    } catch(err: any) {
      console.warn(`Config loaded from ${url} was not valid json. ${err.message}`);
      return;
    }

    this.#setConfigProperties(obj);
  }

  // Updates the config to use values given in the URL query args if they match
  updateFromQueryArgs() {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value: string, key: string) => {
      if (Object.hasOwn(this, key)) {
        let parsedValue: any = undefined;
        // @ts-expect-error
        if (typeof this[key] === 'string') {
          parsedValue = value;
        // @ts-expect-error
        } else if (typeof this[key] === 'number') {
          parsedValue = parseFloat(value);
        // @ts-expect-error
        } else if (typeof this[key] === 'boolean') {
          parsedValue = parseInt(value, 10) != 0;
        }

        // @ts-expect-error
        if (parsedValue !== undefined && this[key] != parsedValue) {
          // @ts-expect-error
          this[key] = parsedValue;
          this.fireChangedEvent(key, parsedValue);
        }
      }
    });
  }

  resetToDefaults() {
    let storageName = this.configStorageName;
    if (!storageName) { throw new Error('Config does not specify a storage name'); }
    this.#setConfigProperties(CONFIG_DEFAULTS.get(storageName));
  }

  watch(...properties: string[]): ConfigWatcher {
    return new ConfigWatcher(this, ...properties);
  }
}


interface ConfigKeyValuePairs {
  [id: string]: any
}

/**
 * A ConfigWatcher is read-only Config that monitors a given subset of the properties of one or more
 * base Configs. This filters the configRevision property and 'change' events to only respond to the
 * given properties, which in turn makes it less noisy when updating dependent values.
 *
 * Typically a ConfigWatcher is created from the base Config with the watch() method:
 *
 * ```js
 *  class SampleConfig extends Config {
 *    foo = 10;
 *    bar = 'test';
 *    baz = false;
 *  }
 *  const config = Config.Create(SampleConfig);
 *
 *  const watched = config.watch('foo', 'baz');
 *  console.log(watched.foo); // 10
 *  config.foo = 12;
 *  console.log(watched.foo); // 12
 * ```
 */
export class ConfigWatcher extends Config implements ConfigKeyValuePairs {
  constructor(config?: Config, ...properties: string[]) {
    super();
    if (config) {
      this.addWatch(config, ...properties);
    }
  }

  get configStorageName(): string | null {
    return null;
  }

  addWatch(config: Config, ...properties: string[]) {
    for (const property of properties) {
      if (Object.hasOwn(this, property)) {
        throw new Error(`ConfigWatcher is already watching a property named ${property}`);
      }
      // @ts-expect-error
      this[property] = config[property];
    }

    config.addEventListener('changed', (event: Event) => {
      const detail = (event as CustomEvent<ConfigEventDetail>).detail;
      if (Object.hasOwn(this, detail.property)) {
        // @ts-expect-error
        this[detail.property] = detail.value;
        this.fireChangedEvent(detail.property, detail.value);
      }
    });
  }
}
